import { useState } from 'react';
import { Modal, Table, TableWrap, THead, TBody, TR, TH, TD } from '@/components/ui';
import { formatMoneyText } from '@/components/ui/Money';
import { NewBusinessBreakdownModal } from '@/components/commissions/NewBusinessAccounts';
import { cx } from '@/lib/cx';
import { formatDateOnly } from '@/lib/format';
import {
  EARNED_HEADING,
  NEW_BUSINESS_LABELS,
  PROGRESS_LABELS,
  PROGRESS_MODAL_TITLE,
  PROGRESS_REGION_HEADINGS,
  earnedRows,
  excludedNote,
  contributionBar as contributionBarOf,
  gateLabel,
  hasEarned,
  hasNewBusiness,
  isRemainder,
  personalTargetLabel,
  progressBar,
  regionShare,
  type CommissionProgressNewBusinessBlock,
  type ProgressBar,
} from '@/lib/commission-progress';
import type {
  CommissionEarned,
  CommissionProgress,
  CommissionProgressRegion,
  RemainderTargetBreakdown,
} from '@/lib/types';
import { ProgressMeter } from './ProgressMeter';
import { CompactMoney } from './TargetTiles';

/*
 * The detail behind the Invoiced tile's commission strip on the University
 * Partnerships dashboard: exact figures for the dashboard's own person against
 * their commission assignment's targets, over the assignment's full period, in
 * the scheme's currency.
 *
 * SUPERSEDED (owner 2026-10-27): "never show pool or payout here". The server
 * now returns `earned` only to the person themselves and to `commissions.read`
 * scope all, so the "Commission earned" block shows personal commission,
 * regional bonus (gate met / not met) and payout. Rates, gate percentages, the
 * scheme name and (owner 2026-10-27) the commission model stay off this modal.
 *
 * Remainder ("derived") target users (`kind: 'remainder'`) have no assignment:
 * personal block only, titled by `personalTargetLabel`, plus the "Target
 * breakdown" table from `remainder.components` (each scheme's remainder in its
 * own currency, converted into the base currency at `rateDate`; never summed
 * here).
 *
 * Owner 2026-10-27: every money figure here is compact K/M (`CompactMoney`,
 * from `compactMoneyParts`), with the exact amount on hover and to a screen
 * reader. Percentages are unchanged.
 *
 * New business (owner 2026-10-28): when the response carries `newBusiness`
 * (`undefined` = older API, `null` = not measured; either way nothing renders),
 * a block shows Accounts this period, Same accounts last period, New business
 * and New business commission (`earned.newBusinessCommission`; `—` for a
 * remainder user or when absent), plus the previous window as a date fact.
 * Each figure opens the one `NewBusinessBreakdownModal` mounted here.
 * Owner 2026-10-28 (configurable gate): New business commission appears in
 * exactly one place, the Commission earned rows when they carry it (the block
 * shows it only when they do not), and the block adds "New business gate"
 * (Gate met / Gate not met) from `earned.newBusinessGateMet` when sent.
 */

function pctText(pct: number | null): string {
  return pct === null ? '—' : `${pct}%`;
}

/** Server attainment percentages are decimal strings; shown as-is, null → `—`. */
function attainmentText(value: string | null): string {
  return value === null ? '—' : `${value}%`;
}

const BREAKDOWN_HEADING = 'Target breakdown';

const BREAKDOWN_HEADINGS = {
  scheme: 'Scheme',
  regional: 'Regional target',
  personal: 'Individual targets',
  remainder: 'Remainder',
  converted: 'Converted',
} as const;

const NEW_BUSINESS_GATE_LABEL = 'New business gate';

const ATTAINMENT_LABELS = {
  personal: 'Personal attainment',
  regional: 'Regional attainment',
} as const;

function EarnedBlock({
  progress,
  earned,
}: {
  progress: CommissionProgress;
  earned: CommissionEarned;
}) {
  const rows = earnedRows(progress);
  const { currency } = progress;
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="m-0 text-sm font-semibold text-fg">{EARNED_HEADING}</h3>
      <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 rounded-sm border border-border bg-surface p-4 text-sm">
        {rows.map((row) => {
          const isPayout = row.key === 'payout';
          return (
            <div key={row.key} className="contents">
              <dt
                className={cx(
                  'min-w-0 [overflow-wrap:anywhere]',
                  isPayout ? 'border-t border-border pt-1.5 font-semibold text-fg' : 'text-fg-muted',
                )}
              >
                {row.label}
                {row.note !== null && (
                  <span className="ml-2 text-xs font-normal text-fg-subtle">{row.note}</span>
                )}
              </dt>
              <dd
                className={cx(
                  'm-0 text-right tabular-nums',
                  isPayout ? 'border-t border-border pt-1.5 font-semibold text-fg' : 'text-fg-secondary',
                )}
              >
                <CompactMoney amount={row.amount} currency={currency} />
              </dd>
            </div>
          );
        })}
      </dl>
      <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-xs">
        <dt className="text-fg-muted">{ATTAINMENT_LABELS.personal}</dt>
        <dd className="m-0 text-right text-fg-secondary tabular-nums">
          {attainmentText(earned.personalAttainmentPercent)}
        </dd>
        <dt className="text-fg-muted">{ATTAINMENT_LABELS.regional}</dt>
        <dd className="m-0 text-right text-fg-secondary tabular-nums">
          {attainmentText(earned.regionalAttainmentPercent)}
        </dd>
      </dl>
    </section>
  );
}

function NewBusinessFigure({
  label,
  amount,
  currency,
  onOpen,
}: {
  label: string;
  amount: string | null;
  currency: string;
  onOpen: () => void;
}) {
  const text = amount?.trim() ?? '';
  return (
    <div className="contents">
      <dt className="min-w-0 text-fg-muted [overflow-wrap:anywhere]">{label}</dt>
      <dd className="m-0 text-right text-fg-secondary tabular-nums">
        {text === '' ? (
          <span>—</span>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            aria-label={`${label}: ${formatMoneyText(text, currency)}`}
            className="cursor-pointer rounded-sm border-0 bg-transparent p-0 text-inherit underline-offset-4 decoration-hairline-strong hover:underline focus-visible:underline"
          >
            <CompactMoney amount={text} currency={currency} />
          </button>
        )}
      </dd>
    </div>
  );
}

function NewBusinessBlock({
  block,
  commission,
  showCommission,
  gateMet,
  currency,
  onOpen,
}: {
  block: CommissionProgressNewBusinessBlock;
  commission: string | null;
  /** False when the Commission earned rows already show the figure (one place only). */
  showCommission: boolean;
  /** `undefined` = older API or no earned figures: the fact is not shown. */
  gateMet: boolean | undefined;
  currency: string;
  onOpen: () => void;
}) {
  const note = excludedNote(
    block.accountsThisPeriod.excludedDocuments + block.accountsLastPeriod.excludedDocuments,
  );
  const previous = `${formatDateOnly(block.previousWindow.from)} – ${formatDateOnly(block.previousWindow.to)}`;
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="m-0 text-sm font-semibold text-fg">{NEW_BUSINESS_LABELS.newBusiness}</h3>
      <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 rounded-sm border border-border bg-surface p-4 text-sm">
        <NewBusinessFigure
          label={NEW_BUSINESS_LABELS.accountsThisPeriod}
          amount={block.accountsThisPeriod.actual}
          currency={currency}
          onOpen={onOpen}
        />
        <NewBusinessFigure
          label={NEW_BUSINESS_LABELS.accountsLastPeriod}
          amount={block.accountsLastPeriod.actual}
          currency={currency}
          onOpen={onOpen}
        />
        <NewBusinessFigure
          label={NEW_BUSINESS_LABELS.newBusiness}
          amount={block.newBusiness}
          currency={currency}
          onOpen={onOpen}
        />
        {showCommission && (
          <NewBusinessFigure
            label={NEW_BUSINESS_LABELS.newBusinessCommission}
            amount={commission}
            currency={currency}
            onOpen={onOpen}
          />
        )}
      </dl>
      <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-xs">
        <dt className="text-fg-muted">Previous window</dt>
        <dd className="m-0 text-right text-fg-secondary tabular-nums">{previous}</dd>
        {gateMet !== undefined && (
          <>
            <dt className="text-fg-muted">{NEW_BUSINESS_GATE_LABEL}</dt>
            <dd className="m-0 text-right text-fg-secondary">{gateLabel(gateMet)}</dd>
          </>
        )}
      </dl>
      {note !== null && <p className="m-0 text-xs text-fg-subtle">{note}</p>}
    </section>
  );
}

function ConvertedCell({ amount, currency }: { amount: string | null; currency: string }) {
  return <CompactMoney amount={amount} currency={currency} />;
}

function TargetBreakdown({
  breakdown,
  baseCurrency,
}: {
  breakdown: RemainderTargetBreakdown;
  baseCurrency: string;
}) {
  const { components, rateDate } = breakdown;
  if (components.length === 0) return null;
  const rateLine = `Rate date ${rateDate}`;
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="m-0 text-sm font-semibold text-fg">{BREAKDOWN_HEADING}</h3>

      <div className="hidden md:block">
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{BREAKDOWN_HEADINGS.scheme}</TH>
                <TH numeric>{BREAKDOWN_HEADINGS.regional}</TH>
                <TH numeric>{BREAKDOWN_HEADINGS.personal}</TH>
                <TH numeric>{BREAKDOWN_HEADINGS.remainder}</TH>
                <TH numeric>{BREAKDOWN_HEADINGS.converted}</TH>
              </TR>
            </THead>
            <TBody>
              {components.map((component) => (
                <TR key={component.schemeId}>
                  <TD>{component.schemeName}</TD>
                  <TD numeric>
                    <CompactMoney amount={component.regionalTarget} currency={component.currency} />
                  </TD>
                  <TD numeric>
                    <CompactMoney amount={component.personalTargets} currency={component.currency} />
                  </TD>
                  <TD numeric>
                    <CompactMoney amount={component.remainder} currency={component.currency} />
                  </TD>
                  <TD numeric>
                    <ConvertedCell amount={component.remainderConverted} currency={baseCurrency} />
                  </TD>
                </TR>
              ))}
              <TR>
                <TD colSpan={5} className="text-xs text-fg-muted">
                  {rateLine}
                </TD>
              </TR>
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="m-0 flex list-none flex-col divide-y divide-border border-y border-border p-0 md:hidden">
        {components.map((component) => (
          <li key={component.schemeId} className="flex flex-col gap-1.5 py-3">
            <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
              {component.schemeName}
            </p>
            <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-xs">
              <dt className="text-fg-muted">{BREAKDOWN_HEADINGS.regional}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <CompactMoney amount={component.regionalTarget} currency={component.currency} />
              </dd>
              <dt className="text-fg-muted">{BREAKDOWN_HEADINGS.personal}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <CompactMoney amount={component.personalTargets} currency={component.currency} />
              </dd>
              <dt className="text-fg-muted">{BREAKDOWN_HEADINGS.remainder}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <CompactMoney amount={component.remainder} currency={component.currency} />
              </dd>
              <dt className="text-fg-muted">{BREAKDOWN_HEADINGS.converted}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <ConvertedCell amount={component.remainderConverted} currency={baseCurrency} />
              </dd>
            </dl>
          </li>
        ))}
        <li className="py-2 text-xs text-fg-muted">{rateLine}</li>
      </ul>
    </section>
  );
}

function ProgressBlock({
  label,
  bar,
  currency,
  excluded,
}: {
  label: string;
  bar: ProgressBar;
  currency: string;
  excluded: number;
}) {
  const note = excludedNote(excluded);
  return (
    <section className="flex min-w-0 flex-col gap-2 bg-surface p-4">
      <h3 className="m-0 text-xs font-medium text-fg-muted">{label}</h3>
      <p className="m-0 min-w-0 text-2xl font-semibold text-fg tabular-nums">
        <CompactMoney amount={bar.actual} currency={currency} />
      </p>
      <p className="m-0 text-xs text-fg-muted">
        of{' '}
        {bar.target === null ? (
          <span>—</span>
        ) : (
          <CompactMoney amount={bar.target} currency={currency} className="text-fg-secondary" />
        )}
      </p>
      <ProgressMeter pct={bar.pct} size="lg" label={label} />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
        <span className="font-medium text-fg-secondary tabular-nums">{pctText(bar.pct)}</span>
        {bar.remaining !== null && (
          <span className="text-fg-muted">
            Remaining <CompactMoney amount={bar.remaining} currency={currency} className="text-fg-secondary" />
          </span>
        )}
      </div>
      {note !== null && <p className="m-0 text-xs text-fg-subtle">{note}</p>}
    </section>
  );
}

function regionShareText(region: CommissionProgressRegion): string {
  return pctText(regionShare(region));
}

function RegionSplit({
  regions,
  currency,
}: {
  regions: readonly CommissionProgressRegion[];
  currency: string;
}) {
  const excludedTotal = regions.reduce(
    (sum, region) => sum + region.regional.excludedDocuments,
    0,
  );
  const note = excludedNote(excludedTotal);
  return (
    <section className="flex flex-col gap-2">
      <h3 className="m-0 text-sm font-semibold text-fg">By region</h3>

      <div className="hidden md:block">
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{PROGRESS_REGION_HEADINGS.region}</TH>
                <TH numeric>{PROGRESS_REGION_HEADINGS.regional}</TH>
                <TH numeric>{PROGRESS_REGION_HEADINGS.contribution}</TH>
                <TH numeric>{PROGRESS_REGION_HEADINGS.share}</TH>
              </TR>
            </THead>
            <TBody>
              {regions.map((region) => (
                <TR key={region.slug}>
                  <TD>{region.name}</TD>
                  <TD numeric>
                    <CompactMoney amount={region.regional.actual} currency={currency} />
                  </TD>
                  <TD numeric>
                    <CompactMoney amount={region.contribution.actual} currency={currency} />
                  </TD>
                  <TD numeric>{regionShareText(region)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="m-0 flex list-none flex-col divide-y divide-border border-y border-border p-0 md:hidden">
        {regions.map((region) => (
          <li key={region.slug} className="flex flex-col gap-1.5 py-3">
            <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{region.name}</p>
            <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-xs">
              <dt className="text-fg-muted">{PROGRESS_REGION_HEADINGS.regional}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <CompactMoney amount={region.regional.actual} currency={currency} />
              </dd>
              <dt className="text-fg-muted">{PROGRESS_REGION_HEADINGS.contribution}</dt>
              <dd className="m-0 text-right text-fg-secondary">
                <CompactMoney amount={region.contribution.actual} currency={currency} />
              </dd>
              <dt className="text-fg-muted">{PROGRESS_REGION_HEADINGS.share}</dt>
              <dd className="m-0 text-right text-fg-secondary tabular-nums">
                {regionShareText(region)}
              </dd>
            </dl>
          </li>
        ))}
      </ul>

      {note !== null && <p className="m-0 text-xs text-fg-subtle">{note}</p>}
    </section>
  );
}

export function CommissionProgressModal({
  open,
  onClose,
  progress,
}: {
  open: boolean;
  onClose: () => void;
  progress: CommissionProgress;
}) {
  const remainderUser = isRemainder(progress);
  const { currency, personal } = progress;
  // A remainder user shows the personal block only, whatever else the wire carries.
  // Owner 2026-10-27: a remainder user's combined regional figure is shown too.
  const regional = progress.regional;
  const contribution = remainderUser ? null : progress.contribution;
  const regions = remainderUser ? [] : progress.regions;
  const personalBar = progressBar(personal.actual, personal.target);
  const regionalBar = regional === null ? null : progressBar(regional.actual, regional.target);
  const contributionBar = remainderUser ? null : contributionBarOf(progress);
  const breakdown = remainderUser ? (progress.remainder ?? null) : null;
  const blockCount = 1 + (regionalBar === null ? 0 : 1) + (contributionBar === null ? 0 : 1);
  const newBusiness = hasNewBusiness(progress) ? progress.newBusiness : null;
  // Remainder users earn no commission on new business; an absent key is an older API.
  const newBusinessCommission =
    !remainderUser && hasEarned(progress) ? (progress.earned.newBusinessCommission ?? null) : null;
  // The New business commission figure lives in exactly one place: the earned rows when they carry it.
  const commissionInEarnedRows =
    !remainderUser && earnedRows(progress).some((row) => row.key === 'newBusiness');
  const newBusinessGateMet =
    !remainderUser && hasEarned(progress) ? progress.earned.newBusinessGateMet : undefined;
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  return (
    <>
    <Modal open={open} onClose={onClose} size="xl" title={PROGRESS_MODAL_TITLE}>
      <div className="flex min-w-0 flex-col gap-5">
        <p className="m-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
          {progress.period.name} · {currency}
        </p>

        <div
          className={cx(
            'grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border',
            // One column per block shown, so a missing block never leaves a bare hairline cell.
            blockCount === 3 ? 'md:grid-cols-3' : blockCount === 2 ? 'md:grid-cols-2' : undefined,
          )}
        >
          <ProgressBlock
            label={personalTargetLabel(progress)}
            bar={personalBar}
            currency={currency}
            excluded={personal.excludedDocuments}
          />
          {regional !== null && regionalBar !== null && (
            <ProgressBlock
              label={PROGRESS_LABELS.regional}
              bar={regionalBar}
              currency={currency}
              excluded={regional.excludedDocuments}
            />
          )}
          {contribution !== null && contributionBar !== null && (
            <ProgressBlock
              label={PROGRESS_LABELS.contribution}
              bar={contributionBar}
              currency={currency}
              excluded={contribution.excludedDocuments}
            />
          )}
        </div>

        {!remainderUser && hasEarned(progress) && (
          <EarnedBlock progress={progress} earned={progress.earned} />
        )}

        {newBusiness !== null && (
          <NewBusinessBlock
            block={newBusiness}
            commission={newBusinessCommission}
            showCommission={!commissionInEarnedRows}
            gateMet={newBusinessGateMet}
            currency={currency}
            onOpen={() => setBreakdownOpen(true)}
          />
        )}

        {breakdown !== null && <TargetBreakdown breakdown={breakdown} baseCurrency={currency} />}

        {regions.length > 0 && <RegionSplit regions={regions} currency={currency} />}
      </div>
    </Modal>
    {newBusiness !== null && (
      <NewBusinessBreakdownModal
        open={open && breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        params={{ userId: progress.userId, periodId: progress.period.id }}
        title={NEW_BUSINESS_LABELS.newBusiness}
      />
    )}
    </>
  );
}
